// Copyright (c) Ping. All rights reserved.
//
// Sessions pane wire adapter. BE exposes:
//   - GET  /v1/auth/sessions          → list of devices (this account)
//   - POST /v1/auth/logout            (services/api/internal/auth/logout)
//     · device_id + refresh        → revoke single device
//     · ?all_devices=true          → revoke every device on the account

import { get, post } from '@core/network/api';

export interface LogoutBody {
  readonly refreshToken: string;
  readonly deviceId: string;
}

/**
 * Sign out of every refresh-token-bearing device on the account.
 * The current device's refresh token is also revoked — the caller
 * is expected to clear local state immediately after.
 *
 * Wire shape: `LogoutRequest { refresh_token }` (see
 * services/api/internal/auth/types.go). The handler decodes JSON
 * with `DisallowUnknownFields`, so extra keys like `device_id`
 * trip a 400 invalid_request.
 */
export async function logoutAllDevices(body: LogoutBody): Promise<void> {
  await post<void>('/v1/auth/logout?all_devices=true', {
    refresh_token: body.refreshToken,
  });
}

/**
 * Sign out the current device only. The BE looks the device up via
 * the refresh token's stored claims; no `device_id` needed in the
 * body (see comment on [[logoutAllDevices]] for the wire shape).
 */
export async function logoutCurrentDevice(body: LogoutBody): Promise<void> {
  await post<void>('/v1/auth/logout', {
    refresh_token: body.refreshToken,
  });
}

/**
 * Bulk per-device sign-out. Revokes every refresh token for the
 * supplied base64url device ids — provided they belong to the
 * account that owns `refreshToken` (the BE silently ignores
 * cross-account ids to prevent griefing).
 *
 * Used by Settings → Sessions multi-select "Log out".
 */
export async function logoutDevices(body: LogoutBody, deviceIds: readonly string[]): Promise<void> {
  if (deviceIds.length === 0) return;
  await post<void>('/v1/auth/logout', {
    refresh_token: body.refreshToken,
    device_ids: deviceIds,
  });
}

/**
 * Listed device entry returned by [[listSessions]]. Shape mirrors
 * `services/api/internal/auth/types.go SessionDeviceWire`.
 */
export interface SessionDevice {
  readonly deviceId: string;
  readonly label: string;
  readonly platform: 'ios' | 'android' | 'macos' | 'web' | 'windows';
  readonly createdAt: string;
  readonly lastSeenAt: string;
  readonly isCurrent: boolean;
}

interface SessionsListResponseWire {
  readonly devices: ReadonlyArray<{
    readonly device_id: string;
    readonly label: string;
    readonly platform: SessionDevice['platform'];
    readonly created_at: string;
    readonly last_seen_at: string;
    readonly is_current: boolean;
  }>;
}

/**
 * Fetch every non-revoked device on the calling account. Used by
 * the Settings → Sessions pane to render the full device list and
 * flag which row is the current device.
 */
export async function listSessions(): Promise<SessionDevice[]> {
  const resp = await get<SessionsListResponseWire>('/v1/auth/sessions');
  return resp.devices.map((d) => ({
    deviceId: d.device_id,
    label: d.label,
    platform: d.platform,
    createdAt: d.created_at,
    lastSeenAt: d.last_seen_at,
    isCurrent: d.is_current,
  }));
}
